import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Search, RefreshCw, Eye, ArrowUpDown, X, ChevronDown,
    TrendingUp, TrendingDown, ScrollText, Download,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

import ReportDateRangePicker from '../../components/ReportDateRangePicker';

const PERIODS = [
    { id: 'today', label: 'Today' },
    { id: 'week',  label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'year',  label: 'This Year' },
    { id: 'all',   label: 'All Time' },
];

const SORT_KEYS = [
    { id: 'net_house_profit', label: 'Net House Profit' },
    { id: 'loaded',           label: 'Loaded In' },
    { id: 'redeemed',         label: 'Redeemed Out' },
    { id: 'wagered',          label: 'Total Wagered' },
    { id: 'pending',          label: 'Pending Cashout' },
    { id: 'name',             label: 'Name (A→Z)' },
];

const fmt = (n) => (n || 0).toFixed(2);

const formatTs = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
    }) + ' CT';
};

export const PlayerPnLTab = () => {
    const [period, setPeriod] = useState('all');
    const [dateRange, setDateRange] = useState({ start: null, end: null, startIso: null, endIso: null });
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState('net_house_profit');
    const [sortDir, setSortDir] = useState('desc');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [drillUser, setDrillUser] = useState(null);
    const [drillData, setDrillData] = useState(null);
    const [drillLoading, setDrillLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (dateRange.startIso && dateRange.endIso) {
                params.set('start_date', dateRange.startIso);
                params.set('end_date', dateRange.endIso);
            } else {
                params.set('period', period);
            }
            const { data } = await axios.get(
                `${API}/admin/reports/player-pnl?${params}`,
                { withCredentials: true }
            );
            setRows(data.rows || []);
            setGeneratedAt(data.generated_at);
        } catch (e) {
            toast.error('Failed to load Player P&L');
        }
        setLoading(false);
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [period, dateRange.startIso, dateRange.endIso]);

    const exportCSV = () => {
        if (!sorted.length) {
            toast.error('No rows to export');
            return;
        }
        const csvEscape = (v) => {
            const s = String(v ?? '');
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const headers = [
            'Name', 'Email', 'Status', 'Balance',
            'Loaded In', 'Redeemed Out', 'Pending Cashout',
            'Sports Wagered', 'Sports Paid Out',
            'Lottery Wagered', 'Lottery Paid Out',
            'Total Wagered', 'Total Bet Payouts',
            'Free Play Given', 'Net House Profit',
        ];
        const rowsCsv = sorted.map(r => [
            r.name, r.email, r.status || '', fmt(r.credits || 0),
            fmt(r.loaded.total), fmt(r.redeemed.total), fmt(r.pending.total),
            fmt(r.sports.wagered), fmt(r.sports.paid_out),
            fmt(r.lottery.wagered), fmt(r.lottery.paid_out),
            fmt((r.sports.wagered || 0) + (r.lottery.wagered || 0)),
            fmt((r.sports.paid_out || 0) + (r.lottery.paid_out || 0)),
            fmt(r.free_play.total), fmt(r.net_house_profit),
        ].map(csvEscape).join(','));
        const csv = [headers.map(csvEscape).join(','), ...rowsCsv].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
        a.href = url;
        a.download = `player-pnl-${period}-${stamp}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${sorted.length} row${sorted.length === 1 ? '' : 's'}`);
    };

    const openDrill = async (row) => {
        setDrillUser(row);
        setDrillData(null);
        setDrillLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/admin/reports/player-pnl/${row.user_id}/transactions`,
                { withCredentials: true }
            );
            setDrillData(data);
        } catch {
            toast.error('Failed to load player timeline');
        }
        setDrillLoading(false);
    };

    const filtered = rows.filter(r => {
        const q = search.toLowerCase().trim();
        if (!q) return true;
        return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
    });

    const sorted = [...filtered].sort((a, b) => {
        let av, bv;
        switch (sortKey) {
            case 'name': return a.name.localeCompare(b.name) * (sortDir === 'asc' ? 1 : -1);
            case 'loaded':   av = a.loaded.total;   bv = b.loaded.total;   break;
            case 'redeemed': av = a.redeemed.total; bv = b.redeemed.total; break;
            case 'wagered':  av = a.sports.wagered + a.lottery.wagered; bv = b.sports.wagered + b.lottery.wagered; break;
            case 'pending':  av = a.pending.total; bv = b.pending.total; break;
            case 'net_house_profit':
            default: av = a.net_house_profit; bv = b.net_house_profit;
        }
        return (av - bv) * (sortDir === 'asc' ? 1 : -1);
    });

    const totals = sorted.reduce((acc, r) => {
        acc.loaded   += r.loaded.total;
        acc.redeemed += r.redeemed.total;
        acc.pending  += r.pending.total;
        acc.wagered  += r.sports.wagered + r.lottery.wagered;
        acc.paid_out += r.sports.paid_out + r.lottery.paid_out;
        acc.free_play+= r.free_play.total;
        acc.net      += r.net_house_profit;
        return acc;
    }, { loaded: 0, redeemed: 0, pending: 0, wagered: 0, paid_out: 0, free_play: 0, net: 0 });

    return (
        <div className="space-y-6">
            <div className="card-dark p-4 sm:p-6 border-[#FFD700]/20">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2">
                            <ScrollText className="w-4 h-4 text-[#FFD700]" />
                            <h3 className="text-lg font-bold text-white">Player P&L Log</h3>
                        </div>
                        <p className="text-[11px] text-[#A0AAB2] mt-1 max-w-xl">
                            Per-player profit/loss breakdown for troubleshooting. See which player loaded what, redeemed what, wagered/won/lost, and the net house profit on each one.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[#A0AAB2] hidden sm:inline" data-testid="pnl-generated-at">
                            {generatedAt ? `Updated: ${formatTs(generatedAt)}` : ''}
                        </span>
                        <Button size="sm" variant="outline" onClick={fetchData}
                            className="border-[#FFD700]/30 text-[#FFD700]" data-testid="pnl-refresh-btn">
                            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                        <Button size="sm" variant="outline" onClick={exportCSV}
                            disabled={!sorted.length}
                            className="border-[#39FF14]/30 text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-40"
                            data-testid="pnl-csv-export-btn">
                            <Download className="w-3 h-3 mr-1" />
                            CSV
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-4">
                    <div className="md:col-span-5">
                        <Label className="text-[#A0AAB2] text-[10px]">Search</Label>
                        <div className="relative mt-1">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A0AAB2]" />
                            <Input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Name or email…"
                                className="bg-[#0a0a0a] border-white/10 text-white pl-7 h-9"
                                data-testid="pnl-search"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A0AAB2] hover:text-white"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="md:col-span-4">
                        <Label className="text-[#A0AAB2] text-[10px]">Period</Label>
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                            {PERIODS.map(p => (
                                <button
                                    type="button"
                                    key={p.id}
                                    onClick={() => { setDateRange({ start: null, end: null, startIso: null, endIso: null }); setPeriod(p.id); }}
                                    disabled={!!dateRange.startIso}
                                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${
                                        !dateRange.startIso && period === p.id
                                            ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                            : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/40 disabled:opacity-40'
                                    }`}
                                    data-testid={`pnl-period-${p.id}`}>
                                    {p.label}
                                </button>
                            ))}
                            <ReportDateRangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                testId="pnl-date-picker"
                            />
                        </div>
                    </div>
                    <div className="md:col-span-3">
                        <Label className="text-[#A0AAB2] text-[10px]">Sort by</Label>
                        <div className="flex gap-1 mt-1">
                            <select value={sortKey} onChange={e => setSortKey(e.target.value)}
                                className="bg-[#0a0a0a] border border-white/10 rounded-lg text-white text-xs px-2 h-9 flex-1"
                                data-testid="pnl-sort-key">
                                {SORT_KEYS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
                            </select>
                            <button
                                type="button"
                                onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                                className="px-2.5 rounded-lg bg-[#0a0a0a] border border-white/10 text-white hover:border-[#FFD700]/40"
                                title={`Sort ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
                                data-testid="pnl-sort-dir-btn">
                                <ArrowUpDown className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Totals strip */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4 text-xs" data-testid="pnl-totals">
                    <Stat label="Loaded In"     value={`+${fmt(totals.loaded)}`}   color="#39FF14" />
                    <Stat label="Redeemed Out"  value={`-${fmt(totals.redeemed)}`} color="#FF3B30" />
                    <Stat label="Pending"       value={`~${fmt(totals.pending)}`}  color="#FFD700" />
                    <Stat label="Wagered"       value={fmt(totals.wagered)}        color="#3B82F6" />
                    <Stat label="Bet Payouts"   value={`-${fmt(totals.paid_out)}`} color="#FF3B30" />
                    <Stat label="Free Play"     value={`-${fmt(totals.free_play)}`}color="#A855F7" />
                    <Stat label="Net House"     value={`${totals.net >= 0 ? '+' : ''}${fmt(totals.net)}`}
                        color={totals.net >= 0 ? '#39FF14' : '#FF3B30'} bold />
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-lg border border-white/5">
                    {loading && rows.length === 0 ? (
                        <div className="text-center py-10 text-[#A0AAB2] text-sm">Loading…</div>
                    ) : sorted.length === 0 ? (
                        <div className="text-center py-10 text-[#A0AAB2] text-sm">No players match.</div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10 bg-[#0a0a0a]">
                                    <TableHead className="text-[#A0AAB2]">Player</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Balance</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Loaded</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Redeemed</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Pending</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Wagered</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Bet Wins</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Free Play</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Net</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Drill</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sorted.map(r => {
                                    const wagered = r.sports.wagered + r.lottery.wagered;
                                    const paid_out = r.sports.paid_out + r.lottery.paid_out;
                                    const positive = r.net_house_profit >= 0;
                                    return (
                                        <TableRow key={r.user_id} className="border-white/5 hover:bg-white/5"
                                            data-testid={`pnl-row-${r.user_id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {positive ? (
                                                        <TrendingUp className="w-3 h-3 text-[#39FF14]" />
                                                    ) : (
                                                        <TrendingDown className="w-3 h-3 text-[#FF3B30]" />
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="text-white text-sm font-bold truncate max-w-[180px]">{r.name}</p>
                                                        <p className="text-[10px] text-[#A0AAB2] truncate max-w-[180px]">{r.email}</p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-white text-xs">{fmt(r.credits)}</TableCell>
                                            <TableCell className="text-right font-mono text-[#39FF14] text-xs">+{fmt(r.loaded.total)}</TableCell>
                                            <TableCell className="text-right font-mono text-[#FF3B30] text-xs">-{fmt(r.redeemed.total)}</TableCell>
                                            <TableCell className="text-right font-mono text-[#FFD700] text-xs">{r.pending.count > 0 ? `~${fmt(r.pending.total)}` : '—'}</TableCell>
                                            <TableCell className="text-right font-mono text-white text-xs">{fmt(wagered)}</TableCell>
                                            <TableCell className="text-right font-mono text-[#FF3B30] text-xs">-{fmt(paid_out)}</TableCell>
                                            <TableCell className="text-right font-mono text-[#A855F7] text-xs">-{fmt(r.free_play.total)}</TableCell>
                                            <TableCell className={`text-right font-mono font-bold text-xs ${positive ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                {positive ? '+' : ''}{fmt(r.net_house_profit)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button size="sm" variant="ghost" onClick={() => openDrill(r)}
                                                    className="h-7 w-7 p-0 text-[#A0AAB2] hover:text-[#FFD700] hover:bg-[#FFD700]/10"
                                                    title="View transaction timeline"
                                                    data-testid={`pnl-drill-${r.user_id}`}>
                                                    <Eye className="w-3.5 h-3.5" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </div>
                <p className="text-[10px] text-[#666] mt-2">
                    Showing {sorted.length} player(s) · {period === 'all' ? 'all-time' : PERIODS.find(p => p.id === period)?.label.toLowerCase()} window
                </p>
            </div>

            {/* Drill-down dialog */}
            <Dialog open={!!drillUser} onOpenChange={(o) => { if (!o) { setDrillUser(null); setDrillData(null); } }}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            {drillUser?.name} <span className="text-[#A0AAB2] text-xs font-normal">({drillUser?.email})</span>
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Full transaction timeline (newest first). Drill in to spot the exact problem.
                        </DialogDescription>
                    </DialogHeader>
                    {drillLoading && (
                        <div className="text-center py-10 text-[#A0AAB2] text-sm">
                            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />Loading timeline…
                        </div>
                    )}
                    {drillData && (
                        <div className="space-y-3" data-testid="pnl-drill-content">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                <Stat label="Balance" value={fmt(drillData.user.credits)} color="#FFD700" />
                                <Stat label="Loaded" value={`+${fmt(drillUser.loaded.total)}`} color="#39FF14" />
                                <Stat label="Redeemed" value={`-${fmt(drillUser.redeemed.total)}`} color="#FF3B30" />
                                <Stat label="Net" value={`${drillUser.net_house_profit >= 0 ? '+' : ''}${fmt(drillUser.net_house_profit)}`}
                                    color={drillUser.net_house_profit >= 0 ? '#39FF14' : '#FF3B30'} bold />
                            </div>
                            <div className="rounded-lg border border-white/10 max-h-[55vh] overflow-y-auto">
                                <table className="w-full text-xs">
                                    <thead className="sticky top-0 bg-[#0a0a0a] z-10">
                                        <tr className="text-left text-[#A0AAB2]">
                                            <th className="p-2">When</th>
                                            <th className="p-2">Kind</th>
                                            <th className="p-2 text-right">Amount</th>
                                            <th className="p-2">Status</th>
                                            <th className="p-2">Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {drillData.timeline.map((t, i) => (
                                            <tr key={i} className="border-t border-white/5 align-top">
                                                <td className="p-2 text-[#A0AAB2] whitespace-nowrap">{formatTs(t.created_at)}</td>
                                                <td className="p-2">
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${kindColor(t.kind)}`}>
                                                        {(t.type || t.kind).replace(/_/g, ' ').toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className={`p-2 text-right font-mono ${(t.amount || 0) >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                    {(t.amount || 0) >= 0 ? '+' : ''}{fmt(t.amount)}
                                                </td>
                                                <td className="p-2">
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${statusColor(t.status)}`}>
                                                        {(t.status || '—').toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="p-2 text-white max-w-md">
                                                    <p className="break-words">{t.description}</p>
                                                    {t.payout_method && (
                                                        <p className="text-[10px] text-[#FFD700] mt-0.5">
                                                            via {t.payout_method}{t.payout_handle ? ` (${t.payout_handle})` : ''}
                                                        </p>
                                                    )}
                                                    {typeof t.balance_after === 'number' && (
                                                        <p className="text-[10px] text-[#A0AAB2]">balance after: {fmt(t.balance_after)}</p>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-[10px] text-[#666]">
                                {drillData.timeline.length} entries · sorted newest first · all times Central
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

const Stat = ({ label, value, color, bold }) => (
    <div className="bg-[#0a0a0a] rounded-lg p-2.5">
        <p className="text-[9px] uppercase font-bold text-[#A0AAB2] tracking-wide">{label}</p>
        <p className={`font-mono ${bold ? 'font-black text-base' : 'font-bold text-sm'}`} style={{ color }}>{value}</p>
    </div>
);

const kindColor = (k) => {
    switch (k) {
        case 'sports_bet':     return 'bg-[#3B82F6]/15 text-[#3B82F6]';
        case 'lottery_ticket': return 'bg-[#F59E0B]/15 text-[#F59E0B]';
        case 'promo':          return 'bg-[#A855F7]/15 text-[#A855F7]';
        default:               return 'bg-white/10 text-white';
    }
};

const statusColor = (s) => {
    switch (s) {
        case 'won':       case 'approved':  case 'credited': return 'bg-[#39FF14]/15 text-[#39FF14]';
        case 'lost':      case 'rejected':  return 'bg-[#FF3B30]/15 text-[#FF3B30]';
        case 'pending':                     return 'bg-[#FFD700]/15 text-[#FFD700]';
        case 'cashed_out':                  return 'bg-[#00BFFF]/15 text-[#00BFFF]';
        default:                            return 'bg-white/10 text-[#A0AAB2]';
    }
};
